import React, { forwardRef, useState, useEffect, useMemo } from 'react';
import { ViewStyle, StyleProp, Platform } from 'react-native';
import { Box } from '@/components/gluestack/box';
import { VStack } from '@/components/gluestack/vstack';
import { Text } from '@/components/gluestack/text';
import { cssInterop } from 'nativewind';
import { Image } from 'expo-image';
import { Center } from '@/components/gluestack/center';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import { PlayIcon } from '@hugeicons-pro/core-solid-rounded';
import { Pressable } from 'react-native';
import { useVisibilityDetection } from '@/hooks/useVisibilityDetection';
import { useIsFocused } from 'expo-router/react-navigation';

// Detect iOS and Safari once at module level for consistency across all component instances
const isIOSPlatform = Platform.OS === 'ios';
const isIOSBrowser =
  typeof navigator !== 'undefined' &&
  /iPad|iPhone|iPod/.test(navigator.userAgent) &&
  !(window as any).MSStream;
const isSafari =
  typeof navigator !== 'undefined' &&
  /Safari/.test(navigator.userAgent) &&
  !/Chrome/.test(navigator.userAgent);
const IS_IOS = isIOSPlatform || isIOSBrowser;
const IS_IOS_SAFARI = IS_IOS && isSafari;

// Helper hooks to access the memoized platform detection
const useIsIOS = () => IS_IOS;
const useIsIOSSafari = () => IS_IOS_SAFARI;

export interface IYouTubePlayerProps {
  videoId: string;
  height?: number;
  width?: string | number;
  className?: string;
  style?: ViewStyle;
  title?: string;
  /**
   * When set to true, the player cleanup resources when the component becomes invisible
   * This is useful when used in virtual lists like FlashList
   */
  unloadOnInvisible?: boolean;
  /**
   * When set to true, uses 16:9 aspect ratio instead of fixed height
   */
  useAspectRatio?: boolean;
}

export const YouTubePlayer = forwardRef<HTMLIFrameElement, IYouTubePlayerProps>(
  (
    {
      videoId,
      height = 240,
      width = '100%',
      className = '',
      style,
      title,
      unloadOnInvisible = true,
      useAspectRatio = false,
    },
    ref
  ) => {
    // Get platform states
    const isIOS = useIsIOS();
    const isIOSSafari = useIsIOSSafari();

    // Initialize showVideo state to false to always show thumbnail first
    const [showVideo, setShowVideo] = useState(false);
    const { ref: containerRef, isVisible } = useVisibilityDetection({ threshold: 0.3 });
    const iframeRef = React.useRef<HTMLIFrameElement>(null);
    const isFocused = useIsFocused();

    // Handle visibility changes
    useEffect(() => {
      if (!isVisible && showVideo && iframeRef.current) {
        // Pause the video immediately when it becomes invisible
        iframeRef.current.contentWindow?.postMessage(
          JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }),
          '*'
        );

        // Then unload if requested
        if (unloadOnInvisible) {
          setShowVideo(false);
        }
      }
    }, [isVisible, unloadOnInvisible, showVideo]);

    // Pause video when screen loses focus (navigation)
    useEffect(() => {
      if (!isFocused && showVideo && iframeRef.current) {
        // Pause the video when navigating away from the screen
        iframeRef.current.contentWindow?.postMessage(
          JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }),
          '*'
        );
      }
    }, [isFocused, showVideo]);

    // Cleanup on unmount
    useEffect(() => {
      return () => {
        if (showVideo) {
          setShowVideo(false);
        }
      };
    }, []);

    // Get thumbnail URL (use hqdefault for reliable thumbnail access)
    const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;

    const handlePress = () => {
      setShowVideo(true);
    };

    // Build YouTube embed URL with appropriate parameters for the platform
    const getYouTubeEmbedUrl = () => {
      // Base parameters that work for all platforms
      const params = [
        'rel=0',
        'controls=1',
        'modestbranding=1',
        'fs=1',
        'enablejsapi=1',
        'origin=' + encodeURIComponent(window.location.origin),
        'autoplay=1', // Autoplay enabled for all platforms
        'playsinline=1', // Plays inline for all platforms
      ];

      return `https://www.youtube.com/embed/${videoId}?${params.join('&')}`;
    };

    return (
      <VStack ref={containerRef} className={`overflow-hidden ${className}`}>
        {title && <Text className="mb-1 text-sm font-medium text-typography-700">{title}</Text>}

        <Box
          className="overflow-hidden rounded-lg"
          style={
            {
              height: useAspectRatio ? undefined : height,
              width: typeof width === 'number' ? width : width,
              ...(useAspectRatio && {
                aspectRatio: 16 / 9,
              }),
            } as StyleProp<ViewStyle>
          }>
          {!showVideo ? (
            <Pressable onPress={handlePress} className="h-full w-full">
              <VStack className="relative h-full">
                <Image
                  source={{ uri: thumbnailUrl }}
                  className="h-full w-full"
                  contentFit="cover"
                />
                <Center className="absolute inset-0 bg-black/20">
                  <Box className="rounded-full bg-black/40 p-4">
                    <HugeIconsWrapper
                      icon={PlayIcon}
                      size={36}
                      className="-mr-0.5 ml-0.5 text-white"
                    />
                  </Box>
                </Center>
              </VStack>
            </Pressable>
          ) : (
            <iframe
              ref={(element) => {
                // Combine refs
                if (ref) {
                  if (typeof ref === 'function') {
                    ref(element);
                  } else {
                    ref.current = element;
                  }
                }
                iframeRef.current = element;
              }}
              src={getYouTubeEmbedUrl()}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
              allowFullScreen
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                backgroundColor: '#000',
              }}
              title={title || `YouTube video ${videoId}`}
            />
          )}
        </Box>
      </VStack>
    );
  }
);

YouTubePlayer.displayName = 'YouTubePlayer';

// Apply NativeWind CSS interop
cssInterop(YouTubePlayer, {
  className: {
    target: 'style',
  },
});
